import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Plus, Trash2, WifiOff, PackagePlus } from 'lucide-react';
import { CONTENT_CARD } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { CatalogSelector } from '@/components/quotes/CatalogSelector';
import { parseQuantityInput } from '@/lib/quantity';
import type { Product } from '@/types/database';
import {
  bundleTotalCost,
  bundleTotalPrice,
  canBeBundleMaterial,
  canHaveBundleMaterials,
  resolveBundleItems,
  type BundleItemRow,
} from '@/lib/productBundles';

interface BundleMaterialsSectionProps {
  /** Id do serviço em edição — vazio quando ainda não foi salvo. */
  parentId: string;
  /** Catálogo plano (pais e variações), para resolver os materiais. */
  products: Product[];
  /** Ids de produtos que TÊM variações (não são vendáveis, logo não são materiais). */
  parentsWithVariants: Set<string>;
  /** Ids que já são kits (não podem virar material — um nível só). */
  kitParentIds: Set<string>;
  /** Ids que já são material de algum kit (não podem virar kit). */
  materialIds: Set<string>;
  value: BundleItemRow[];
  onChange: (rows: BundleItemRow[]) => void;
  isOnline: boolean;
  companyId: string | null;
}

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

/**
 * "Materiais inclusos" — o bloco do modal do SERVIÇO que monta o kit.
 *
 * Reusa o `CatalogSelector` (o mesmo seletor do orçamento) em vez de uma lista
 * própria: ele já resolve variações, já lê do cache offline e já é o
 * vocabulário visual que o usuário conhece.
 *
 * O que ele NÃO faz: gravar. O componente só edita um rascunho; quem persiste é
 * o `handleSave` da CatalogPage, junto com o resto do item — assim um serviço
 * novo consegue receber materiais (que precisam do id real do pai) na mesma
 * ação, e um erro de gravação não deixa metade do kit salvo.
 */
export function BundleMaterialsSection({
  parentId,
  products,
  parentsWithVariants,
  kitParentIds,
  materialIds,
  value,
  onChange,
  isOnline,
  companyId,
}: BundleMaterialsSectionProps) {
  const [selectorOpen, setSelectorOpen] = useState(false);
  /** Itens que o seletor devolveu mas que o "nível único" não deixa entrar. */
  const [rejectedNames, setRejectedNames] = useState<string[]>([]);

  const resolved = useMemo(() => resolveBundleItems(value, products), [value, products]);
  const { total: totalCost, missingCost } = useMemo(() => bundleTotalCost(resolved), [resolved]);
  const totalPrice = useMemo(() => bundleTotalPrice(resolved), [resolved]);

  // O outro lado do "nível único": um item que já é material de outro kit não
  // pode ter materiais próprios. O banco recusaria, então a UI nem oferece.
  const blockedAsMaterial = parentId ? !canHaveBundleMaterials(parentId, materialIds) : false;

  const handleSelect = (selected: Product[]) => {
    const existing = new Set(value.map((r) => String(r.child_product_id)));
    const rejected: string[] = [];

    const additions: BundleItemRow[] = [];
    for (const product of selected) {
      const id = String(product.id);
      if (existing.has(id)) continue;
      if (
        !canBeBundleMaterial(product, {
          parentId,
          parentsWithVariants,
          kitParentIds,
        })
      ) {
        rejected.push(product.name);
        continue;
      }
      additions.push({
        company_id: companyId,
        parent_product_id: parentId,
        child_product_id: id,
        quantity: 1,
      });
      existing.add(id);
    }

    if (additions.length > 0) onChange([...value, ...additions]);
    // Nada de recusa silenciosa: se algum item não pôde entrar, o usuário
    // precisa saber qual e por quê (CLAUDE.md: no silent caps).
    if (rejected.length > 0) {
      setRejectedNames(rejected);
    } else {
      setRejectedNames([]);
    }
  };

  /**
   * QUANTIDADE tem parser próprio (`parseQuantityInput`, #71/#72) — o
   * `parseFloat(raw.replace(',', '.'))` que estava aqui não é equivalente.
   *
   * A magnitude bate nos dois ("1.5" e "1,5" dão 1,5; o ponto é decimal de
   * propósito, porque o teclado numérico do Android manda ponto). O que muda é
   * a VALIDAÇÃO: `parseFloat` para no primeiro caractere inválido em vez de
   * recusar, então `'5abc'` virava **5** e `'1,5,5'` virava **1,5** — o usuário
   * digitava uma coisa e o kit gravava outra, sem aviso nenhum. O parser
   * oficial recusa os dois, e recusar é o que deixa o erro visível para quem
   * digitou.
   *
   * O contrato de VAZIO também é dele: `{ ok: true, value: null }` é estado de
   * digitação (o usuário apagou para redigitar), não erro — por isso o campo
   * aceita '' e só o `ok: false` preserva o valor anterior.
   */
  const updateQuantity = (childId: string, raw: string) => {
    const { ok, value: parsed } = parseQuantityInput(raw);
    onChange(
      value.map((row) =>
        String(row.child_product_id) === childId
          ? { ...row, quantity: ok ? (parsed ?? '') : row.quantity }
          : row,
      ),
    );
  };

  const removeItem = (childId: string) => {
    onChange(value.filter((row) => String(row.child_product_id) !== childId));
  };

  if (blockedAsMaterial) {
    return (
      <div className={cn(CONTENT_CARD, 'space-y-2 p-4')}>
        <div className="flex items-center gap-2 text-sm font-medium">
          <PackagePlus className="h-4 w-4 text-muted-foreground" />
          Materiais inclusos
        </div>
        <p className="text-xs text-muted-foreground">
          Este item já está incluso em outro serviço, então ele não pode ter materiais próprios
          (um nível só).
        </p>
      </div>
    );
  }

  return (
    <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
      <div className="flex items-center gap-2">
        <PackagePlus className="h-4 w-4 text-muted-foreground" />
        <div>
          <p className="text-sm font-medium">Materiais inclusos</p>
          <p className="text-xs text-muted-foreground">
            Ao escolher este serviço no orçamento, os materiais entram como itens próprios — e
            baixam do estoque.
          </p>
        </div>
      </div>

      {resolved.length > 0 && (
        <div className="space-y-2">
          {resolved.map((item) => {
            const raw = value.find((r) => String(r.child_product_id) === String(item.product.id));
            return (
              <div
                key={String(item.product.id)}
                className="flex items-center gap-2 rounded-md border bg-muted/30 p-2"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{item.displayName}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatBRL(Number(item.product.price ?? 0))} por {item.product.unit || 'un'}
                  </p>
                </div>
                <Input
                  aria-label={`Quantidade de ${item.displayName}`}
                  inputMode="decimal"
                  className="h-9 w-20 text-center"
                  value={String(raw?.quantity ?? '')}
                  onChange={(e) => updateQuantity(String(item.product.id), e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="shrink-0 text-destructive"
                  onClick={() => removeItem(String(item.product.id))}
                  aria-label={`Remover ${item.displayName}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          })}

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <span>Custo dos materiais: {formatBRL(totalCost)}</span>
            <span>Preço somado: {formatBRL(totalPrice)}</span>
          </div>
          {/* Agregado com coluna nullable NUNCA cala a ausência (armadilha #61). */}
          {missingCost > 0 && (
            <p className="text-xs text-amber-600">
              {missingCost === 1
                ? '1 material está sem custo informado e ficou de fora desta conta.'
                : `${missingCost} materiais estão sem custo informado e ficaram de fora desta conta.`}
            </p>
          )}
        </div>
      )}

      {rejectedNames.length > 0 && (
        <p className="text-xs text-amber-600">
          Não foi possível incluir: {rejectedNames.join(', ')}. Produtos com variações precisam ser
          escolhidos pela variação, e um serviço que já tem materiais não pode virar material.
        </p>
      )}

      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() => setSelectorOpen(true)}
        disabled={!isOnline}
      >
        <Plus className="mr-2 h-4 w-4" />
        Adicionar material
      </Button>

      {!isOnline && (
        <p className="flex items-center gap-1.5 text-xs text-amber-600">
          <WifiOff className="h-3.5 w-3.5 shrink-0" />
          Gerenciar materiais inclusos exige conexão com a internet.
        </p>
      )}

      <CatalogSelector open={selectorOpen} onOpenChange={setSelectorOpen} onSelect={handleSelect} />
    </div>
  );
}
